// Types
import {
  AffiliateSaleStatus,
  AffiliateSaleType,
} from 'api/graphql-global-types';
import { GetStreamAffiliateLanding_getStreamAffiliateLink } from 'api/streamAffiliate/types/GetStreamAffiliateLanding';
import { GetMyStreamAffiliateLinks_getMyStreamAffiliateLinks_entities } from 'api/streamAffiliate/types/GetMyStreamAffiliateLinks';
import { StatusVariant } from 'ui3/Status/Status';
// Constants
import { HOME } from 'constants/routes';
// Helpers
import {
  clearAffiliateCodeFromCookies,
  getAffiliateCodeFromCookies,
} from 'helpers/cookies';
import { formatPriceToUSD } from 'helpers/priceText';
import { getEnvLink, getStreamPath } from 'helpers/routes';
import { getTicketEventPath } from 'helpers/ticketSales';
import { pluralize } from 'helpers/string';
// Components
import { showToast } from 'components/common/Toast/Toast';

export const AFFILIATE_LANDING_BASE_PATH = '/a';

const TICKET_EVENT_AFFILIATE_KEY_PREFIX = 'event_';

/** What an affiliate link sells: a stream (its PPV and the tickets of its event) or an event without a stream. */
export type AffiliateOwner = {
  streamId?: string | null;
  ticketEventId?: string | null;
};

export const roundMoney = (value: number): number =>
  Math.round(value * 100) / 100;

/**
 * Commission is a share of the organizer's net (price after the platform fee
 * and promocode), never of the gross price. Mirrors the backend snapshot math.
 */
export const calculateAffiliateCommission = (
  netProfit: number,
  commissionPercent: number
): number => {
  if (!(netProfit > 0) || !(commissionPercent > 0)) {
    return 0;
  }

  return roundMoney((netProfit * commissionPercent) / 100);
};

export const getAffiliateLandingPath = (code: string): string =>
  `${AFFILIATE_LANDING_BASE_PATH}/${code}`;

export const getAffiliateLandingUrl = (code: string): string =>
  `${getEnvLink()}${getAffiliateLandingPath(code)}`;

/**
 * Key of the attribution cookie. An event on a stream runs the stream's program, so the stream wins;
 * event keys are prefixed so they never collide with a stream id.
 */
export const getAffiliateCookieKey = ({
  streamId,
  ticketEventId,
}: AffiliateOwner): string | null => {
  if (streamId) {
    return streamId;
  }

  return ticketEventId
    ? `${TICKET_EVENT_AFFILIATE_KEY_PREFIX}${ticketEventId}`
    : null;
};

export const getAffiliateCodeForOwner = (
  owner: AffiliateOwner
): string | null => {
  const key = getAffiliateCookieKey(owner);

  return key ? getAffiliateCodeFromCookies(key) : null;
};

export const clearAffiliateCodeForOwner = (owner: AffiliateOwner): void => {
  const key = getAffiliateCookieKey(owner);

  if (key) {
    clearAffiliateCodeFromCookies(key);
  }
};

/** A link's stream page, else the page of its event without a stream. */
const getAffiliateEventPath = (
  stream: { slug: string | null; store: { slug: string | null } | null } | null,
  ticketEventSlug: string | null
): string | null => {
  if (stream?.slug && stream.store?.slug) {
    return getStreamPath(stream, 'streams');
  }

  return ticketEventSlug
    ? getTicketEventPath({ slug: ticketEventSlug, stream: null })
    : null;
};

/** Where an affiliate link lands; an unknown or emptied link goes home. */
export const getAffiliateLandingDestination = (
  landing: Pick<
    GetStreamAffiliateLanding_getStreamAffiliateLink,
    'streamSlug' | 'storeSlug' | 'ticketEventSlug'
  > | null
): string =>
  (landing &&
    getAffiliateEventPath(
      { slug: landing.streamSlug, store: { slug: landing.storeSlug } },
      landing.ticketEventSlug
    )) ||
  HOME;

export const copyAffiliateLink = async (code: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(getAffiliateLandingUrl(code));
    showToast({ message: 'Affiliate link copied', autoClose: 1000 });
  } catch (error) {
    showToast({ message: 'Could not copy', type: 'error', autoClose: 1000 });
  }
};

type MyAffiliateLinkRow = GetMyStreamAffiliateLinks_getMyStreamAffiliateLinks_entities;

export const getMyAffiliateEventPath = (
  row: Pick<MyAffiliateLinkRow, 'stream' | 'ticketEventSlug'>
): string | null => getAffiliateEventPath(row.stream, row.ticketEventSlug);

/** "8 PPV Sales · 10 Ticket Sales"; a link without a stream only sells tickets. */
export const formatAffiliateSalesCounts = (
  row: Pick<MyAffiliateLinkRow, 'stream' | 'ppvSalesCount' | 'ticketsSoldCount'>
): string => {
  const parts: string[] = [];

  if (row.stream) {
    parts.push(
      `${row.ppvSalesCount} PPV ${pluralize(row.ppvSalesCount, 'Sale')}`
    );
  }

  if (!row.stream || row.ticketsSoldCount > 0) {
    parts.push(
      `${row.ticketsSoldCount} Ticket ${pluralize(
        row.ticketsSoldCount,
        'Sale'
      )}`
    );
  }

  return parts.join(' · ');
};

/**
 * A PPV sale earns a fixed amount; a ticket sale earns the rate on the tier
 * price, so an event without PPV shows it from its cheapest tier.
 */
export const formatAffiliatePerSale = (
  row: Pick<
    MyAffiliateLinkRow,
    'commissionPerSale' | 'link' | 'ticketPriceFrom'
  >
): string => {
  if (row.commissionPerSale > 0) {
    return formatPriceToUSD(row.commissionPerSale);
  }

  if (row.ticketPriceFrom === null) {
    return '—';
  }

  return `From ${formatPriceToUSD(
    calculateAffiliateCommission(
      row.ticketPriceFrom,
      row.link.commissionPercent
    )
  )}`;
};

export const AFFILIATE_SALE_TYPE_LABELS: Record<AffiliateSaleType, string> = {
  [AffiliateSaleType.Ppv]: 'PPV',
  [AffiliateSaleType.Tickets]: 'Tickets',
};

export type SaleStatusMeta = {
  label: string;
  variant: StatusVariant;
  isRefunded: boolean;
};

/** How a sale's status reads wherever orders and commissions are listed. */
export const SALE_STATUS_META: Record<
  'paid' | 'pending' | 'refunded',
  SaleStatusMeta
> = {
  paid: { label: 'Paid', variant: 'approved', isRefunded: false },
  pending: { label: 'Pending', variant: 'applied', isRefunded: false },
  refunded: { label: 'Refunded', variant: 'dim', isRefunded: true },
};

export const getAffiliateSaleStatus = (
  status: AffiliateSaleStatus
): SaleStatusMeta => {
  switch (status) {
    case AffiliateSaleStatus.Refunded:
      return SALE_STATUS_META.refunded;
    case AffiliateSaleStatus.Pending:
      return SALE_STATUS_META.pending;
    case AffiliateSaleStatus.Paid:
    default:
      return SALE_STATUS_META.paid;
  }
};
